import { useInterval } from "usehooks-ts";
import { getQuote } from "../store/global/getQuote";
import { GlobalStore } from "../store/global/GlobalStore";
import { getOrderType } from "../utils/orderTypeUtils";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCanQuote } from "./useCanQuote";
import { singletonHook } from "react-singleton-hook";
import { QUOTE_REFETCH_INTERVAL_MS } from "../config/consts";
import { useTokenListByChain } from "./useTokenListByChain";

function useTimedQuoteRefetchImpl() {
  const [secondsToExpiry, setSecondsToExpiry] = useState<number>(QUOTE_REFETCH_INTERVAL_MS / 1000);
  const isLoadingQuote = useRef(false);
  const isForceRefreshing = useRef<boolean>(false);
  const autoRefresh = GlobalStore.useState(s => s.autoRefreshQuote);
  const tokenList = useTokenListByChain();
  const {
    makerTokens,
    takerTokens,
    quote,
    network,
    orderType,
    address,
    receiver,
    permitType,
    isFirstQuoteForTrade,
  } = GlobalStore.useState(s => {
    return {
      makerTokens: s.makerTokens,
      takerTokens: s.takerTokens,
      quote: s.quote,
      network: s.network,
      orderType: getOrderType(s.takerTokens, s.makerTokens),
      address: s.address,
      receiver: s.receiver,
      permitType: s.permitType,
      isFirstQuoteForTrade: s.isFirstQuoteForTrade,
    };
  });

  const canQuote = useCanQuote();

  const updateIsFirstQuoteForTrade = useCallback((newValue: boolean) => {
    GlobalStore.update(s => {
      s.isFirstQuoteForTrade = newValue;
    });
  }, []);

  useEffect(() => {
    updateIsFirstQuoteForTrade(true);
  }, [makerTokens, takerTokens, network, updateIsFirstQuoteForTrade]);

  useEffect(() => {
    if (!canQuote) {
      setSecondsToExpiry(0);
    }
  }, [canQuote]);

  const forceRefresh = useCallback(async (): Promise<void> => {
    if (orderType) {
      await getQuote.run({
        orderType,
        takerTokens,
        makerTokens,
        network,
        address: address as `0x${string}`,
        tokenList,
        receiver,
        permitType,
        forceRouteType: isFirstQuoteForTrade ? undefined : quote?.routeType,
      });
      updateIsFirstQuoteForTrade(false);
    }
  }, [
    orderType,
    takerTokens,
    makerTokens,
    network,
    address,
    tokenList,
    receiver,
    permitType,
    isFirstQuoteForTrade,
    quote?.routeType,
    updateIsFirstQuoteForTrade,
  ]);

  const getTimeout = useCallback((): number | null => {
    if (isForceRefreshing.current) {
      return 1;
    }
    return canQuote && autoRefresh ? QUOTE_REFETCH_INTERVAL_MS : null;
  }, [autoRefresh, canQuote]);

  // When quote is refreshed externally - update seconds to expiry
  useEffect(() => {
    if (quote) {
      setSecondsToExpiry(QUOTE_REFETCH_INTERVAL_MS / 1000);
    }
  }, [quote]);

  useInterval(
    () => {
      if (!autoRefresh && secondsToExpiry === 0) {
        GlobalStore.update(s => {
          s.quoteExpired = true;
        });
      }
      setSecondsToExpiry(prev => {
        const newVal = prev - 1;
        return newVal > 0 ? newVal : 0;
      });
    },
    canQuote ? 1000 : null
  );

  useInterval(
    () => {
      if (isForceRefreshing.current) {
        isForceRefreshing.current = false;
      }
      if (quote != null && tokenList != null && orderType != null && !isLoadingQuote.current) {
        isLoadingQuote.current = true;
        void getQuote
          .run({
            orderType,
            takerTokens,
            makerTokens,
            network,
            address: address as `0x${string}`,
            tokenList,
            receiver,
            permitType,
            forceRouteType: isFirstQuoteForTrade ? undefined : quote.routeType,
          })
          .then(() => {
            isLoadingQuote.current = false;
            GlobalStore.update(s => {
              s.quoteExpired = false;
              s.isFirstQuoteForTrade = false;
            });
            setSecondsToExpiry(QUOTE_REFETCH_INTERVAL_MS / 1000);
          })
          .finally(() => {
            isLoadingQuote.current = false;
          });
      }
    },
    // Delay in milliseconds or null to stop it
    getTimeout()
  );

  return { secondsToExpiry, forceRefresh, isLoadingQuote: isLoadingQuote.current };
}

export const useTimedQuoteRefetch = singletonHook(
  {
    secondsToExpiry: QUOTE_REFETCH_INTERVAL_MS,
    forceRefresh: () => {
      return new Promise(resolve => {
        console.log(`noop`);
      });
    },
    isLoadingQuote: false,
  },
  useTimedQuoteRefetchImpl
);
